/**
 * Purpose-specific browser consent contract.
 *
 * The version lives in both the cookie names and durable evidence. Changing a
 * purpose, provider set, or disclosure must therefore mint a new version and
 * can never silently reinterpret an older acceptance. v3 adds Personalised
 * advertising as a genuinely new, separate purpose from Advertising — an
 * existing Advertising acceptance never implied ad_personalization, so this
 * bump correctly re-prompts every category, not just the new one.
 */
export const WEB_CONSENT_VERSION = "web-purpose-v3" as const;

export type WebConsentCategory =
  | "analytics"
  | "advertising"
  | "personalization";
export type WebConsentChoice = "accepted" | "declined";

export const ANALYTICS_CONSENT_NAME = "tsb_analytics_consent_v3";
export const ANALYTICS_CONSENT_UPDATED_AT_NAME =
  "tsb_analytics_consent_updated_at_v3";
export const ADVERTISING_CONSENT_NAME = "tsb_advertising_consent_v3";
export const ADVERTISING_CONSENT_UPDATED_AT_NAME =
  "tsb_advertising_consent_updated_at_v3";
export const PERSONALIZATION_CONSENT_NAME = "tsb_personalization_consent_v3";
export const PERSONALIZATION_CONSENT_UPDATED_AT_NAME =
  "tsb_personalization_consent_updated_at_v3";

/** Legacy binary cookies. They are read only for fail-closed migration. */
export const COOKIE_CONSENT_NAME = "cookie_consent";
export const COOKIE_CONSENT_UPDATED_AT_NAME = "cookie_consent_updated_at";

export const WEB_CONSENT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;
export const CONSENT_CHOICE_MIN_TIMESTAMP_MS = Date.UTC(2020, 0, 1);
export const CONSENT_CHOICE_MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;
export const LEGACY_CONSENT_CHOICE_UPDATED_AT = new Date(
  CONSENT_CHOICE_MIN_TIMESTAMP_MS,
);

export const WEB_CONSENT_COOKIE_NAMES: Record<
  WebConsentCategory,
  { choice: string; updatedAt: string }
> = {
  analytics: {
    choice: ANALYTICS_CONSENT_NAME,
    updatedAt: ANALYTICS_CONSENT_UPDATED_AT_NAME,
  },
  advertising: {
    choice: ADVERTISING_CONSENT_NAME,
    updatedAt: ADVERTISING_CONSENT_UPDATED_AT_NAME,
  },
  personalization: {
    choice: PERSONALIZATION_CONSENT_NAME,
    updatedAt: PERSONALIZATION_CONSENT_UPDATED_AT_NAME,
  },
};

export interface WebConsentCategorySnapshot {
  choice: WebConsentChoice | null;
  updatedAt: Date | null;
  granted: boolean;
  current: boolean;
}

export interface WebConsentSnapshot {
  version: typeof WEB_CONSENT_VERSION;
  analytics: WebConsentCategorySnapshot;
  advertising: WebConsentCategorySnapshot;
  personalization: WebConsentCategorySnapshot;
  complete: boolean;
}

function readCookieValues(
  cookieHeader: string | null | undefined,
  name: string,
): Array<string | null> {
  if (!cookieHeader) return [];
  return cookieHeader.split(";").flatMap((part) => {
    const separator = part.indexOf("=");
    if (separator < 0 || part.slice(0, separator).trim() !== name) return [];
    try {
      return [decodeURIComponent(part.slice(separator + 1).trim())];
    } catch {
      return [null];
    }
  });
}

/**
 * Duplicate path/domain cookies are accepted only when every visible value is
 * the same valid choice. Any ambiguity fails closed.
 */
export function parseNamedConsentChoiceCookie(
  cookieHeader: string | null | undefined,
  name: string,
): WebConsentChoice | null {
  const values = readCookieValues(cookieHeader, name);
  if (values.length === 0 || values.some((value) => value === null))
    return null;
  const first = values[0];
  if (
    (first !== "accepted" && first !== "declined") ||
    values.some((value) => value !== first)
  ) {
    return null;
  }
  return first;
}

export function parseConsentCategoryChoiceCookie(
  cookieHeader: string | null | undefined,
  category: WebConsentCategory,
): WebConsentChoice | null {
  return parseNamedConsentChoiceCookie(
    cookieHeader,
    WEB_CONSENT_COOKIE_NAMES[category].choice,
  );
}

/**
 * Parse a browser-supplied consent-choice timestamp without permitting a
 * far-future value to pin the durable Account preference indefinitely.
 */
export function parseConsentChoiceUpdatedAt(
  value: string | Date | null | undefined,
  nowMs = Date.now(),
): Date | null {
  const timestampMs =
    value instanceof Date ? value.getTime() : Date.parse(value ?? "");
  if (
    !Number.isFinite(timestampMs) ||
    timestampMs < CONSENT_CHOICE_MIN_TIMESTAMP_MS ||
    timestampMs > nowMs + CONSENT_CHOICE_MAX_FUTURE_SKEW_MS
  ) {
    return null;
  }
  return new Date(timestampMs);
}

/** Consent evidence must be both authentic to v2 and renewed every 180 days. */
export function isCurrentWebConsentEvidence(
  version: string | null | undefined,
  updatedAt: Date | null | undefined,
  nowMs = Date.now(),
): boolean {
  if (version !== WEB_CONSENT_VERSION || !updatedAt) return false;
  const timestampMs = updatedAt.getTime();
  return (
    Number.isFinite(timestampMs) &&
    timestampMs >= nowMs - WEB_CONSENT_COOKIE_MAX_AGE_SECONDS * 1000 &&
    timestampMs <= nowMs + CONSENT_CHOICE_MAX_FUTURE_SKEW_MS
  );
}

/** Read a timestamp cookie with the same duplicate-cookie fail-closed rule. */
export function parseNamedConsentUpdatedAtCookie(
  cookieHeader: string | null | undefined,
  name: string,
  nowMs = Date.now(),
): Date | null {
  const values = readCookieValues(cookieHeader, name);
  if (values.length === 0 || values.some((value) => value === null))
    return null;
  const parsed = values.map((value) =>
    parseConsentChoiceUpdatedAt(value, nowMs),
  );
  const firstMs = parsed[0]?.getTime();
  if (
    firstMs === undefined ||
    parsed.some((value) => value === null || value.getTime() !== firstMs)
  ) {
    return null;
  }
  return new Date(firstMs);
}

export function parseConsentCategoryUpdatedAtCookie(
  cookieHeader: string | null | undefined,
  category: WebConsentCategory,
  nowMs = Date.now(),
): Date | null {
  return parseNamedConsentUpdatedAtCookie(
    cookieHeader,
    WEB_CONSENT_COOKIE_NAMES[category].updatedAt,
    nowMs,
  );
}

/** Legacy timestamp parser retained solely for migration and old refusals. */
export function parseConsentChoiceUpdatedAtCookie(
  cookieHeader: string | null | undefined,
  nowMs = Date.now(),
): Date | null {
  return parseNamedConsentUpdatedAtCookie(
    cookieHeader,
    COOKIE_CONSENT_UPDATED_AT_NAME,
    nowMs,
  );
}

export function parseWebConsentSnapshot(
  cookieHeader: string | null | undefined,
  nowMs = Date.now(),
): WebConsentSnapshot {
  function categorySnapshot(
    category: WebConsentCategory,
  ): WebConsentCategorySnapshot {
    const choice = parseConsentCategoryChoiceCookie(cookieHeader, category);
    const updatedAt = parseConsentCategoryUpdatedAtCookie(
      cookieHeader,
      category,
      nowMs,
    );
    const current = isCurrentWebConsentEvidence(
      WEB_CONSENT_VERSION,
      updatedAt,
      nowMs,
    );
    // A choice without its trustworthy ordering evidence never grants.
    return {
      choice,
      updatedAt,
      granted: choice === "accepted" && current,
      current,
    };
  }

  const analytics = categorySnapshot("analytics");
  const advertising = categorySnapshot("advertising");
  const personalization = categorySnapshot("personalization");
  return {
    version: WEB_CONSENT_VERSION,
    analytics,
    advertising,
    personalization,
    complete:
      analytics.choice !== null &&
      analytics.current &&
      advertising.choice !== null &&
      advertising.current &&
      personalization.choice !== null &&
      personalization.current,
  };
}

export function parseLegacyConsentChoiceCookie(
  cookieHeader: string | null | undefined,
): WebConsentChoice | null {
  return parseNamedConsentChoiceCookie(cookieHeader, COOKIE_CONSENT_NAME);
}
